import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Lenis from '@studio-freight/lenis';
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/Header";
import KineticHero from "@/components/KineticHero";
import Marquee from "@/components/Marquee";
import NumberedSection from "@/components/NumberedSection";
import WaitlistForm from "@/components/WaitlistForm";
import Footer from "@/components/Footer";
import ProblemSection from "@/components/ProblemSection";
import TrustSection from "@/components/TrustSection";
import CTASection from "@/components/CTASection";

const Home = () => {
  useEffect(() => {
    // Initialize Lenis smooth scrolling
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 2,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  return (
    <div className="relative">
      <Header />
      <KineticHero />
      <Marquee />
      
      <div id="features" className="bg-slate-950">
        <ProblemSection />
        
        <NumberedSection
          number="01"
          title="WhatsApp Integration"
          description="Health logging made simple. Update your family's health information through WhatsApp—the app your family already uses every day."
          features={[
            "Log health updates via chat",
            "Upload prescriptions and reports instantly",
            "Get AI-powered health reminders",
            "Share updates with family members"
          ]}
          image="/clan-health-whatsapp.jpg"
          imageVariant="standalone"
        />

        <NumberedSection
          number="02"
          title="Unified Dashboard"
          description="See everyone's health in one place. Track appointments, medications, and milestones across all family members from a single, intuitive dashboard."
          features={[
            "Multi-profile family management",
            "Health timeline for each member",
            "Medication tracking and reminders",
            "Appointment calendar sync"
          ]}
          image="/clan-health-app-screenshot.jpg"
          reverse={true}
        />

        <NumberedSection
          number="03"
          title="Multi-Generation Care"
          description="From pediatric milestones to geriatric care management. Specialized tracking for every life stage of your family members."
          features={[
            "Pediatric milestone tracking for children",
            "Adult wellness and fitness monitoring",
            "Chronic condition management for elders",
            "Emergency contact and medical history"
          ]}
          image="/clan-health-reminders.jpg"
        />

        <NumberedSection
          number="04"
          title="Integrated Services"
          description="Access complete healthcare services from one platform. Book lab tests, consultations, and get medicines delivered—all managed seamlessly."
          features={[
            "Online pharmacy with doorstep delivery",
            "Home diagnostic services",
            "Tele-consultations with specialists",
            "Home visit scheduling"
          ]}
          visual="services"
          reverse={true}
        />
      </div>

      <div id="trust">
        <TrustSection />
      </div>

      <div id="how-it-works">
        <CTASection />
      </div>

      <div id="contact">
        <WaitlistForm />
      </div>

      <Footer />
      <Toaster />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
